/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React, { useState } from 'react';
import { QrCode, Smartphone, Blocks, ShieldCheck, CheckCircle, ArrowRight, Loader2, Lock, Wifi } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const StepVisual: React.FC<{ step: number }> = ({ step }) => {
    switch (step) {
        case 0: // Merchant Creates
            return (
                <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }} className="flex flex-col items-center">
                    <div className="w-32 h-48 bg-white dark:bg-[#15171C] rounded-xl border-4 border-stone-200 dark:border-[#2D3142] p-4 flex flex-col items-center justify-center shadow-xl relative">
                        <div className="text-xs font-bold mb-2 text-stone-500">GhostPOS</div>
                        <div className="text-xl font-bold text-[#0A0B0F] dark:text-white">$42.50</div>
                        <div className="mt-4 p-2 bg-black dark:bg-white rounded-lg">
                            <QrCode className="text-white dark:text-black" size={48} />
                        </div>
                    </div>
                    <div className="mt-4 px-3 py-1 bg-nobel-gold text-white text-xs font-bold rounded-full">Awaiting Scan</div>
                </motion.div>
            );
        case 1: // Customer Pays
            return (
                <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }} className="relative">
                    <div className="w-32 h-48 bg-black rounded-[24px] border-4 border-stone-800 p-2 shadow-2xl flex flex-col items-center">
                        <div className="w-full h-full bg-[#15171C] rounded-[18px] flex flex-col items-center pt-6 px-2 relative overflow-hidden">
                            <div className="absolute top-0 w-16 h-4 bg-black rounded-b-lg"></div>
                            <div className="text-[10px] text-gray-400 mb-2">Phantom Wallet</div>
                            <div className="w-12 h-12 rounded-full bg-stone-800 flex items-center justify-center mb-2">
                                <ArrowRight size={20} className="text-white -rotate-45"/>
                            </div>
                            <div className="text-center">
                                <div className="text-white font-bold text-sm">Send 0.3 SOL</div>
                                <div className="text-[8px] text-gray-500">To: Ghost Store</div>
                            </div>
                            <div className="mt-auto w-full h-8 bg-blue-500 rounded-md flex items-center justify-center text-[10px] font-bold text-white mb-2">Slide to Pay</div>
                        </div>
                    </div>
                    <Wifi size={24} className="absolute -top-4 -right-4 text-nobel-gold animate-pulse" />
                </motion.div>
            );
        case 2: // Blockchain Confirms
            return (
                <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }} className="grid grid-cols-2 gap-2">
                    {[1,2,3,4].map(i => (
                        <motion.div 
                            key={i}
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: i * 0.1 }}
                            className="w-16 h-16 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center justify-center"
                        >
                            <Blocks size={24} className="text-blue-500" />
                        </motion.div>
                    ))}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-black/80 text-white px-4 py-2 rounded-full text-xs font-bold backdrop-blur-sm border border-white/10 flex items-center gap-2">
                            <Loader2 size={12} className="animate-spin"/> Confirming
                        </div>
                    </div>
                </motion.div>
            );
        case 3: // GhostPay Validates
            return (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4">
                    <div className="w-20 h-20 bg-stone-900 rounded-full flex items-center justify-center border-4 border-nobel-gold shadow-[0_0_30px_rgba(197,160,89,0.3)]">
                        <ShieldCheck size={40} className="text-white"/>
                    </div>
                    <div className="space-y-2 w-48">
                        <div className="flex items-center justify-between text-[10px] text-stone-500 dark:text-stone-400 bg-stone-100 dark:bg-white/5 p-2 rounded">
                            <span>Amount Match</span>
                            <CheckCircle size={12} className="text-green-500"/>
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-stone-500 dark:text-stone-400 bg-stone-100 dark:bg-white/5 p-2 rounded">
                            <span>Token Valid</span>
                            <CheckCircle size={12} className="text-green-500"/>
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-stone-500 dark:text-stone-400 bg-stone-100 dark:bg-white/5 p-2 rounded">
                            <span>Wallet whitelist</span>
                            <CheckCircle size={12} className="text-green-500"/>
                        </div>
                    </div>
                </motion.div>
            );
        case 4: // POS Confirms
            return (
                <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="flex flex-col items-center">
                     <div className="w-32 h-48 bg-white dark:bg-[#15171C] rounded-xl border-4 border-green-500 p-4 flex flex-col items-center justify-center shadow-xl relative overflow-hidden">
                        <div className="absolute inset-0 bg-green-500/10 animate-pulse"></div>
                        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4 z-10">
                            <CheckCircle size={32} className="text-white" />
                        </div>
                        <div className="text-lg font-bold text-green-600 dark:text-green-400 z-10">Paid</div>
                        <div className="text-[10px] text-stone-500 z-10 mt-1">Tx: 8x92...3k1</div>
                    </div>
                </motion.div>
            );
        default: return null;
    }
};

const InteractiveProcessFlow = () => {
    const [activeStep, setActiveStep] = useState(0);
    
    const steps = [
        { icon: QrCode, title: "Initiate", desc: "Merchant generates QR." },
        { icon: Smartphone, title: "Scan & Pay", desc: "Customer signs transaction." },
        { icon: Blocks, title: "On-Chain", desc: "Network processes block." },
        { icon: ShieldCheck, title: "Validate", desc: "GhostOS verifies metadata." },
        { icon: CheckCircle, title: "Settled", desc: "Funds confirmed." }
    ];

    return (
        <div className="bg-stone-50 dark:bg-black/20 border border-stone-200 dark:border-white/10 rounded-2xl p-6 md:p-8">
            <h3 className="font-serif text-xl font-bold text-stone-900 dark:text-white mb-8 text-center">Interactive Process Flow</h3>
            
            <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
                {/* Left: Steps List */}
                <div className="space-y-3">
                    {steps.map((step, i) => (
                        <button
                            key={i}
                            onClick={() => setActiveStep(i)}
                            className={`w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all duration-300 relative overflow-hidden ${
                                activeStep === i 
                                    ? 'bg-white dark:bg-[#1A1D23] shadow-lg border-l-4 border-nobel-gold ring-1 ring-black/5 dark:ring-white/5' 
                                    : 'hover:bg-white/50 dark:hover:bg-white/5 border-l-4 border-transparent opacity-60 hover:opacity-100'
                            }`}
                        >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${activeStep === i ? 'bg-nobel-gold text-white' : 'bg-stone-200 dark:bg-white/10 text-stone-500 dark:text-stone-400'}`}>
                                <step.icon size={20} />
                            </div>
                            <div>
                                <div className={`font-bold text-sm ${activeStep === i ? 'text-stone-900 dark:text-white' : 'text-stone-600 dark:text-stone-400'}`}>{step.title}</div>
                                <div className="text-xs text-stone-500 dark:text-stone-500">{step.desc}</div>
                            </div>
                            {activeStep === i && (
                                <motion.div layoutId="active-indicator" className="absolute right-4 text-nobel-gold">
                                    <ArrowRight size={16} />
                                </motion.div>
                            )}
                        </button>
                    ))}
                </div>

                {/* Right: Visualization */}
                <div className="bg-stone-200 dark:bg-black/40 rounded-2xl h-[320px] flex items-center justify-center border border-stone-300 dark:border-white/5 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #888 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
                    <AnimatePresence mode="wait">
                        <StepVisual key={activeStep} step={activeStep} />
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export const PaymentFlow = () => (
  <div className="space-y-12">
    <section>
        <p className="text-lg text-stone-600 dark:text-stone-300 leading-relaxed font-medium">
            The core in-store and online payment flow is designed for simplicity and security. Explore the lifecycle of a transaction below.
        </p>
    </section>

    <InteractiveProcessFlow />

    <section className="grid md:grid-cols-2 gap-6">
        <div className="p-6 bg-white dark:bg-white/5 rounded-xl border border-stone-200 dark:border-white/10">
            <h4 className="font-bold text-stone-900 dark:text-white mb-2 flex items-center gap-2">
                <Lock size={18} className="text-nobel-gold"/> Security First
            </h4>
            <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
                Every transaction is cryptographically signed by the user's wallet. GhostPay acts as a non-custodial validator, ensuring the on-chain state matches the merchant's invoice before confirming the order.
            </p>
        </div>
        <div className="p-6 bg-white dark:bg-white/5 rounded-xl border border-stone-200 dark:border-white/10">
            <h4 className="font-bold text-stone-900 dark:text-white mb-2 flex items-center gap-2">
                <Wifi size={18} className="text-nobel-gold"/> Real-time Sync
            </h4>
            <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
                Using Websockets and on-chain listeners (RPC), the GhostOS runtime detects payments within milliseconds of block inclusion, providing instant feedback to both the customer and the merchant terminal.
            </p>
        </div>
    </section>
  </div>
);